import { lazy } from 'react'

import ProtectedRoute from '@/components/protected-route'
import { sentryCreateBrowserRouter } from '@/lib/sentry'

const HomePage = lazy(() => import('@/pages/home'))
const SignIn = lazy(() => import('@/pages/sign-in'))
const SignUp = lazy(() => import('@/pages/sign-up'))
const SignOut = lazy(() => import('@/pages/sign-out'))
const LibraryPage = lazy(() => import('@/pages/library'))
const BookPage = lazy(() => import('@/pages/book'))
const BookCreatePage = lazy(() => import('@/pages/book-create'))
const BookDetailPage = lazy(() => import('@/pages/book-detail'))
const ShelfPage = lazy(() => import('@/pages/shelf'))
const TagPage = lazy(() => import('@/pages/tag'))
const LoanPage = lazy(() => import('@/pages/loan'))
const MemberPage = lazy(() => import('@/pages/member'))
const MemberDetailPage = lazy(() => import('@/pages/member-detail'))
const MemberCreatePage = lazy(() => import('@/pages/member-create'))
const RolePage = lazy(() => import('@/pages/role'))
const BillingPage = lazy(() => import('@/pages/billing'))
const SettingPage = lazy(() => import('@/pages/setting'))
const SettingImportPage = lazy(() => import('@/pages/setting/import'))
const SettingSystemPage = lazy(() => import('@/pages/setting/system'))
const TrashPage = lazy(() => import('@/pages/trash'))
const ErrorPage = lazy(() => import('@/pages/error'))
const CallbackPage = lazy(() => import('@/pages/callback'))
const MainLayout = lazy(() => import('@/layouts/main'))
const AuthLayout = lazy(() => import('@/layouts/auth'))
const AuthBaseLayout = lazy(() => import('@/layouts/auth-base'))
const OTPPage = lazy(() => import('@/pages/otp'))
const ForgotPasswordPage = lazy(() => import('@/pages/forgot-password'))
const PasswordChangedPage = lazy(() => import('@/pages/password-changed'))
const ResetPasswordPage = lazy(() => import('@/pages/reset-password'))
const WishlistPage = lazy(() => import('@/pages/wishlist'))
const WishlistDetailPage = lazy(() => import('@/pages/wishlist-detail'))
const ShelfDetailPage = lazy(() => import('@/pages/shelf-detail'))
const ShelfGroupPage = lazy(() => import('@/pages/shelf/group'))
const ItemDetailPage = lazy(() => import('@/pages/item-detail'))
const TagDetailPage = lazy(() => import('@/pages/tag-detail'))
const OrganizationPage = lazy(() => import('@/pages/organization'))
const OrganizationEditPage = lazy(() => import('@/pages/organization/edit'))
const SearchOnlinePage = lazy(() => import('@/pages/search-online'))
const RoleCreatePage = lazy(() => import('@/pages/role-create'))
const RoleDetailPage = lazy(() => import('@/pages/role-detail'))
const AccountProfilePage = lazy(() => import('@/pages/profile'))
const MyQRPage = lazy(() => import('@/pages/my-qr'))
const ChangePasswordPage = lazy(() => import('@/pages/change-password'))
const LoanDetailPage = lazy(() => import('@/pages/loan-detail'))
const NotificationPage = lazy(() => import('@/pages/notification'))
const SwitchTenantPage = lazy(() => import('@/pages/switch-tenant'))
const AcceptInvitationPage = lazy(() => import('@/pages/accept-invitation'))
const PrivacyPolicyPage = lazy(() => import('@/pages/privacy-policy'))
const LibraryDetailPage = lazy(() => import('@/pages/library-detail'))
const DiscoverPage = lazy(() => import('@/pages/discover'))
const DiscoverEventsPage = lazy(() => import('@/pages/discover-events'))
const DiscoverPostCreatePage = lazy(() => import('@/pages/discover-post-create'))
const DiscoverPostDetailPage = lazy(() => import('@/pages/discover-post-detail'))
const DiscoverPostEditPage = lazy(() => import('@/pages/discover-post-edit'))
const DiscoverNewlyTitles = lazy(() => import('@/pages/discover-newly-titles'))
const DiscoverRecommendedTitles = lazy(() => import('@/pages/discover-recommended-titles'))
const SearchMember = lazy(() => import('@/pages/search-member'))
const DiscoverMostBorrowedBooks = lazy(() => import('@/pages/discover-most-borrowed-books'))
const PublicLayout = lazy(() => import('@/layouts/public-layout'))
const PublicLibraryPage = lazy(() => import('@/pages/public-library'))
const PublicBookPage = lazy(() => import('@/pages/public-book'))
const PublicBookDetailPage = lazy(() => import('@/pages/public-book-detail'))
const PublicDiscoverPage = lazy(() => import('@/pages/public-discover'))
const PublicDiscoverEventsPage = lazy(() => import('@/pages/public-discover-events'))
const PublicDiscoverPostDetailPage = lazy(() => import('@/pages/public-discover-post-detail'))
const PublicDiscoverMostBorrowedBooks = lazy(() => import('@/pages/public-discover-most-borrowed-books'))
const PublicDiscoverNewlyTitles = lazy(() => import('@/pages/public-discover-newly-titles'))
const PublicLibraryDetailPage = lazy(() => import('@/pages/public-library-detail'))
const TermsOfService = lazy(() => import('@/pages/terms-of-service'))
const MyRatingsReviews = lazy(() => import('@/pages/my-ratings-reviews'))
const PublicOrganizationPage = lazy(() => import('@/pages/public-organization'))
const PublicTagDetailPage = lazy(() => import('@/pages/public-tag-detail'))
const GenerateCodePage = lazy(() => import('@/pages/generate-code'))
const RegisterAsMemberPage = lazy(() => import('@/pages/register-as-member'))
const RegistrationGuidePage = lazy(() => import('@/pages/registration-guide'))
const MemberRegistrationGuidePage = lazy(() => import('@/pages/member-registration-guide'))
const RegistrationsPage = lazy(() => import('@/pages/registrations'))
const StatisticsPage = lazy(() => import('@/pages/statistics'))
const TopReadersPage = lazy(() => import('@/pages/top-readers'))
const TopVisitPage = lazy(() => import('@/pages/top-most-visit'))
const TopGenresPage = lazy(() => import('@/pages/top-genres'))
const TopAuthorsPage = lazy(() => import('@/pages/top-authors'))
const TopPublishersPage = lazy(() => import('@/pages/top-publishers'))
const FieldSettingPage = lazy(() => import('@/pages/field-setting'))
const TitleFieldsPage = lazy(() => import('@/pages/field-setting/title'))
const MemberCheckInPage = lazy(() => import('@/pages/member-check-in'))
const BackUpPage = lazy(() => import('@/pages/backup'))
const PricingPage = lazy(() => import('@/pages/pricing'))
const SettingNotificationsPage = lazy(() => import('@/pages/setting-notifications'))
const HoldsPage = lazy(() => import('@/pages/holds'))
const HoldShelfPage = lazy(() => import('@/pages/hold-shelf'))
const HoldPullListPage = lazy(() => import('@/pages/hold-pull-list'))
const HoldQueuePage = lazy(() => import('@/pages/hold-queue'))
const FieldsSettingPage = lazy(() => import('@/pages/setting/fields-setting'))
const HoldSettingPage = lazy(() => import('@/pages/setting-hold'))
const SettingLibraryPage = lazy(() => import('@/pages/setting-library'))
const AccountSettingPage = lazy(() => import('@/pages/account-setting'))
const LanguageAndRegionPage = lazy(() => import('@/pages/account-setting/language-and-region'))
const DisplayPage = lazy(() => import('@/pages/account-setting/display'))
const MyProfilePage = lazy(() => import('@/pages/account-setting/my-profile'))
const PricingLayout = lazy(() => import('@/layouts/pricing'))
const ContactPage = lazy(() => import('@/pages/contact'))
const LoanSettingPage = lazy(() => import('@/pages/setting-loans'))

export const ROUTES = {
  HOME: '/',
  SIGN_UP: '/sign-up',
  SIGN_IN: '/sign-in',
  SIGN_OUT: '/sign-out',
  FORGOT_PASSWORD: '/forgot-password',
  OTP: '/verify-otp',
  RESET_PASSWORD: '/reset-password',
  PASSWORD_CHANGED: '/password-changed',
  LOAN: '/loans',
  MEMBER: '/members',
  ROLE: '/roles',
  SETTING: '/settings',
  TRASH: '/trash',
  LIBRARY: '/libraries',
  CALL_BACK: '/callback',
  WISHLIST: '/wishlist',
  BOOK: '/books',
  SHELF: '/shelves',
  SHELF_GROUP: '/shelf-groups',
  ITEM: '/items',
  TAG: '/tags',
  ORGANIZATION: '/organization',
  ORGANIZATION_EDIT: '/organization/edit',
  ACCOUNT: '/account',
  NOTIFICATION: '/notification',
  SWITCH_TENANT: '/switch-tenant',
  ACCEPT_INVITATION: '/acceptinvitation',
  PRICACY_POLICY: '/privacy-policy',
  DISCOVER: '/discover',
  DISCOVER_EVENTS: '/discover/events',
  DISCOVER_POST_DETAIL: '/discover/post-detail',
  DISCOVER_POST_CREATE: '/discover/post-create',
  DISCOVER_POST_EDIT: '/discover/post-edit',
  DISCOVER_NEWLY_TITLES: '/discover/newly-titles',
  DISCOVER_RECOMMENDED_FOR_YOU: '/discover/recommended-for-you',
  DISCOVER_MOST_BORROWED_BOOKS: '/discover/most-borrowed-books',
  SHARE: '/share',
  TERMS_OF_SERVICE: '/terms-of-service',
  PUBLIC_ORGANIZATION: '/share/organization',
  BARCODE_GENERATOR: '/barcode_generator',
  REGISTER: '/share/register',
  REGISTRATION_GUIDE: '/share/registration-guide',
  REGISTRATIONS: '/registrations',
  REGISTRATIONS_GUIDE: '/registrations/guide',
  STATISTICS: '/statistics',
  TOP_READERS: '/statistics/top-readers',
  MOST_VISTITORS: '/statistics/most-visitors',
  TOP_GENRES: '/statistics/top-genres',
  TOP_AUTHORS: '/statistics/top-authors',
  TOP_PUBLISHERS: '/statistics/top-publishers',
  FIELD_SETTING: '/settings/fields',
  MEMBER_CHECK_IN: '/member-check-in',
  LI: '/li',
  BACK_UP: '/back-up',
  PRICING: '/pricing',
  HOLDS: '/holds',
  ACCOUNT_SETTING: '/account-setting',
  CONTACT: '/contact',
}

export const router = sentryCreateBrowserRouter([
  {
    path: ROUTES.HOME,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: ROUTES.LIBRARY,
        children: [
          {
            index: true,
            element: <LibraryPage />,
          },
          { path: ':libraryKey', element: <LibraryDetailPage /> },
          {
            path: ':libraryId',
            children: [
              {
                path: 'books',
                children: [
                  { index: true, element: <BookPage /> },
                  { path: 'create', element: <BookCreatePage /> },
                  { path: 'search', element: <SearchOnlinePage /> },
                ],
              },
              {
                path: 'setting-library',
                children: [
                  { element: <SettingLibraryPage />, index: true },

                  { path: 'notifications', element: <SettingNotificationsPage /> },

                  {
                    path: 'fields-setting',
                    element: <FieldsSettingPage />,
                  },
                  {
                    path: 'hold-setting',
                    element: <HoldSettingPage />,
                  },
                  {
                    path: 'loan-setting',
                    element: <LoanSettingPage />,
                  },
                ],
              },
              {
                path: 'shelves',
                element: <ShelfPage />,
              },
              {
                path: 'tags',
                element: <TagPage />,
              },
              {
                path: 'wishlist',
                element: <WishlistPage />,
              },
            ],
          },
        ],
      },
      {
        path: ROUTES.WISHLIST,
        children: [
          {
            index: true,
            element: <WishlistPage />,
          },
          { path: ':titleKey', element: <WishlistDetailPage /> },
        ],
      },
      {
        path: ROUTES.BOOK,
        children: [
          {
            path: ':titleKey',
            children: [
              {
                index: true,
                element: <BookDetailPage />,
              },
              { path: 'hold-queue', element: <HoldQueuePage /> },
            ],
          },
        ],
      },
      {
        path: ROUTES.SHELF,
        children: [{ path: ':shelfKey', element: <ShelfDetailPage /> }],
      },
      {
        path: ROUTES.SHELF_GROUP,
        children: [{ path: ':shelfKey', element: <ShelfGroupPage /> }],
      },
      {
        path: ROUTES.ITEM,
        children: [{ path: ':itemKey', element: <ItemDetailPage /> }],
      },
      {
        path: ROUTES.TAG,
        children: [{ path: ':tagKey', element: <TagDetailPage /> }],
      },
      {
        path: ROUTES.LOAN,
        children: [
          {
            index: true,
            element: <LoanPage />,
          },
          {
            path: ':loanKey',
            element: <LoanDetailPage />,
          },
        ],
      },
      {
        path: ROUTES.HOLDS,
        children: [
          {
            index: true,
            element: <HoldsPage />,
          },
          {
            path: 'hold-shelf',
            element: <HoldShelfPage />,
          },

          {
            path: 'pull-list',
            element: <HoldPullListPage />,
          },
        ],
      },
      {
        path: ROUTES.BARCODE_GENERATOR,
        element: <GenerateCodePage />,
      },
      {
        path: ROUTES.STATISTICS,
        children: [
          {
            index: true,
            element: <StatisticsPage />,
          },
          {
            path: 'top-readers',
            element: <TopReadersPage />,
          },
          {
            path: 'most-visitors',
            element: <TopVisitPage />,
          },
          {
            path: 'top-genres',
            element: <TopGenresPage />,
          },
          {
            path: 'top-authors',
            element: <TopAuthorsPage />,
          },
          {
            path: 'top-publishers',
            element: <TopPublishersPage />,
          },
        ],
      },
      {
        path: ROUTES.DISCOVER,
        children: [
          {
            index: true,
            element: <DiscoverPage />,
          },
          {
            path: 'events/:libraryId',
            element: <DiscoverEventsPage />,
          },
          {
            path: 'post-detail/:postKey',
            element: <DiscoverPostDetailPage />,
          },
          {
            path: 'post-create/:libraryId',
            element: <DiscoverPostCreatePage />,
          },
          { path: 'post-edit/:postKey', element: <DiscoverPostEditPage /> },
          { path: 'newly-titles/:libraryId', element: <DiscoverNewlyTitles /> },
          { path: 'most-borrowed-books/:libraryId', element: <DiscoverMostBorrowedBooks /> },
          { path: 'recommended-for-you/:libraryId', element: <DiscoverRecommendedTitles /> },
        ],
      },
      {
        path: ROUTES.MEMBER,
        children: [
          { element: <MemberPage />, index: true },
          {
            path: ':memberKey',
            element: <MemberDetailPage />,
          },
          { path: 'create', element: <MemberCreatePage /> },
          { path: 'search', element: <SearchMember /> },
        ],
      },
      {
        path: ROUTES.MEMBER_CHECK_IN,
        children: [{ element: <MemberCheckInPage />, index: true }],
      },
      {
        path: ROUTES.ROLE,
        children: [
          { index: true, element: <RolePage /> },
          { path: 'create', element: <RoleCreatePage /> },
          { path: ':roleKey', element: <RoleDetailPage /> },
        ],
      },
      {
        path: ROUTES.REGISTRATIONS,
        children: [
          { index: true, element: <RegistrationsPage /> },
          { path: 'guide', element: <MemberRegistrationGuidePage /> },
        ],
      },
      {
        path: ROUTES.SETTING,
        children: [
          { element: <SettingPage />, index: true },
          {
            path: 'import',
            element: <SettingImportPage />,
          },
          { path: 'systems', element: <SettingSystemPage /> },
          { path: 'notifications', element: <SettingNotificationsPage /> },
          {
            path: 'fields',
            children: [
              { element: <FieldSettingPage />, index: true },
              {
                path: 'title',
                element: <TitleFieldsPage />,
              },
            ],
          },
          {
            path: 'fields-setting',
            element: <FieldsSettingPage />,
          },
          {
            path: 'hold-setting',
            element: <HoldSettingPage />,
          },
          {
            path: 'loan-setting',
            element: <LoanSettingPage />,
          },
          {
            path: 'backup',
            element: <BackUpPage />,
          },
          {
            path: 'billing',
            children: [
              { element: <BillingPage />, index: true },
              {
                path: 'contact',
                element: <ContactPage />,
              },
            ],
          },
        ],
      },
      { path: 'billings', element: <BillingPage /> },
      {
        path: ROUTES.TRASH,
        element: <TrashPage />,
      },
      { path: ROUTES.ORGANIZATION, element: <OrganizationPage /> },
      { path: ROUTES.ORGANIZATION_EDIT, element: <OrganizationEditPage /> },
      {
        path: ROUTES.ACCOUNT_SETTING,
        children: [
          { index: true, element: <AccountSettingPage /> },
          { path: 'language-and-region', element: <LanguageAndRegionPage /> },
          { path: 'display', element: <DisplayPage /> },
          {
            path: 'my-profile',
            children: [
              { index: true, element: <MyProfilePage /> },
              { path: 'profile', element: <AccountProfilePage /> },
              { path: 'change-password', element: <ChangePasswordPage /> },
              { path: 'my-qr', element: <MyQRPage /> },
              { path: 'my-ratings-reviews', element: <MyRatingsReviews /> },
            ],
          },
        ],
      },

      {
        path: ROUTES.NOTIFICATION,
        element: <NotificationPage />,
      },
    ],
  },
  {
    element: <AuthBaseLayout />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: ROUTES.SIGN_UP,
            element: <SignUp />,
          },
          {
            path: ROUTES.SIGN_IN,
            element: <SignIn />,
          },
        ],
      },
      {
        path: ROUTES.OTP,
        element: <OTPPage />,
      },
      {
        path: ROUTES.FORGOT_PASSWORD,
        element: <ForgotPasswordPage />,
      },
      {
        path: ROUTES.RESET_PASSWORD,
        element: <ResetPasswordPage />,
      },
      {
        path: ROUTES.PASSWORD_CHANGED,
        element: <PasswordChangedPage />,
      },
      {
        path: ROUTES.TERMS_OF_SERVICE,
        element: <TermsOfService />,
      },
      {
        path: ROUTES.PRICACY_POLICY,
        element: <PrivacyPolicyPage />,
      },
    ],
  },
  {
    element: <PricingLayout />,
    children: [
      {
        path: ROUTES.PRICING,
        element: <PricingPage />,
      },
      {
        path: ROUTES.CONTACT,
        element: <ContactPage />,
      },
    ],
  },
  { path: `${ROUTES.SWITCH_TENANT}/:tenantId`, element: <SwitchTenantPage /> },
  {
    path: ROUTES.CALL_BACK,
    element: <CallbackPage />,
  },
  {
    path: ROUTES.SIGN_OUT,
    element: <SignOut />,
  },
  {
    path: ROUTES.ACCEPT_INVITATION,
    element: <AcceptInvitationPage />,
  },
  {
    path: ROUTES.SHARE,
    element: (
      <ProtectedRoute>
        <PublicLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'organization/:tenantId',
        element: <PublicLibraryPage />,
      },
      {
        path: 'library/:shardId',
        element: <PublicLibraryPage />,
      },
      {
        path: 'library/:shardId/detail',
        element: <PublicLibraryDetailPage />,
      },
      {
        path: 'library/:shardId/books',
        element: <PublicBookPage />,
      },
      {
        path: 'books/:titleKey',
        element: <PublicBookDetailPage />,
      },
      {
        path: 'organization',
        element: <PublicOrganizationPage />,
      },
      {
        path: 'tags/:tagKey',
        element: <PublicTagDetailPage />,
      },
      {
        path: 'discover',

        children: [
          {
            path: ':shardId',
            element: <PublicDiscoverPage />,
          },
          {
            path: 'events/:libraryKey',
            element: <PublicDiscoverEventsPage />,
          },
          {
            path: 'post-detail/:postKey',
            element: <PublicDiscoverPostDetailPage />,
          },
          { path: 'newly-titles/:libraryKey', element: <PublicDiscoverNewlyTitles /> },
          { path: 'most-borrowed-books/:libraryKey', element: <PublicDiscoverMostBorrowedBooks /> },
        ],
      },
      { path: 'register', element: <RegisterAsMemberPage /> },
      { path: 'registration-guide', element: <RegistrationGuidePage /> },
    ],
  },

  {
    path: ':id',
    element: (
      <ProtectedRoute>
        <PublicLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <PublicLibraryPage /> },
      { path: 'libraries', element: <PublicLibraryPage /> },
      {
        path: 'library/:shardId',
        element: <PublicLibraryPage />,
      },
      {
        path: 'library/:shardId/detail',
        element: <PublicLibraryDetailPage />,
      },
      {
        path: 'library/:shardId/books',
        element: <PublicBookPage />,
      },
      {
        path: 'books/:titleKey',
        element: <PublicBookDetailPage />,
      },
      {
        path: 'organization',
        element: <PublicOrganizationPage />,
      },
      {
        path: 'tags/:tagKey',
        element: <PublicTagDetailPage />,
      },
      {
        path: 'discover',

        children: [
          {
            path: ':shardId',
            element: <PublicDiscoverPage />,
          },
          {
            path: 'events/:libraryKey',
            element: <PublicDiscoverEventsPage />,
          },
          {
            path: 'post-detail/:postKey',
            element: <PublicDiscoverPostDetailPage />,
          },
          { path: 'newly-titles/:libraryKey', element: <PublicDiscoverNewlyTitles /> },
          { path: 'most-borrowed-books/:libraryKey', element: <PublicDiscoverMostBorrowedBooks /> },
        ],
      },
      { path: 'register', element: <RegisterAsMemberPage /> },
      { path: 'registration-guide', element: <RegistrationGuidePage /> },
    ],
  },
  {
    path: `${ROUTES.LI}/:id`,
    element: (
      <ProtectedRoute>
        <PublicLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <PublicBookPage /> },
      { path: 'libraries', element: <PublicLibraryPage /> },
      {
        path: 'library/:shardId',
        element: <PublicLibraryPage />,
      },
      {
        path: 'library/:shardId/detail',
        element: <PublicLibraryDetailPage />,
      },
      {
        path: 'library/:shardId/books',
        element: <PublicBookPage />,
      },
      {
        path: 'books/:titleKey',
        element: <PublicBookDetailPage />,
      },
      {
        path: 'organization',
        element: <PublicOrganizationPage />,
      },
      {
        path: 'tags/:tagKey',
        element: <PublicTagDetailPage />,
      },
      {
        path: 'discover',

        children: [
          {
            path: ':shardId',
            element: <PublicDiscoverPage />,
          },
          {
            path: 'events/:libraryKey',
            element: <PublicDiscoverEventsPage />,
          },
          {
            path: 'post-detail/:postKey',
            element: <PublicDiscoverPostDetailPage />,
          },
          { path: 'newly-titles/:libraryKey', element: <PublicDiscoverNewlyTitles /> },
          { path: 'most-borrowed-books/:libraryKey', element: <PublicDiscoverMostBorrowedBooks /> },
        ],
      },
      { path: 'register', element: <RegisterAsMemberPage /> },
      { path: 'registration-guide', element: <RegistrationGuidePage /> },
    ],
  },
])

interface Route {
  path: string
  label: string
}
export const linkContacts = 'https://bookshare.app/en/contact'

export const footerLinks: Route[] = [
  {
    path: 'https://bookshare.app',
    label: 'about_us',
  },
  {
    path: ROUTES.SIGN_UP,
    label: 'sign_up',
  },
  {
    path: ROUTES.SIGN_IN,
    label: 'sign_in',
  },
  {
    path: 'https://skoolib.notion.site/',
    label: 'help',
  },
  {
    path: 'https://bookshare.app/blog',
    label: 'blog',
  },
  {
    path: ROUTES.PRICING,
    label: 'pricing',
  },
  {
    path: ROUTES.PRICACY_POLICY,
    label: 'privacy_policy',
  },
  {
    path: ROUTES.TERMS_OF_SERVICE,
    label: 'terms',
  },
  {
    path: ROUTES.CONTACT,
    label: 'contact',
  },
]
